import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Grid,
  Chip
} from '@mui/material';
import {
  NavigateNext,
  NavigateBefore,
  Save,
  CheckCircle
} from '@mui/icons-material';
import { profileService } from '../services/profileService';
import { useNotification } from '../contexts/NotificationContext';

const Onboarding = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    personal: {
      age: '28',
      nationality: 'Indian',
      maritalStatus: 'single',
      dependents: 0
    },
    education: {
      level: "Bachelor's Degree",
      field: 'Computer Science',
      institution: 'University of Technology',
      country: 'India',
      year: '2018'
    },
    workExperience: {
      totalYears: '5',
      currentRole: 'Software Engineer',
      industry: 'Technology/IT',
      skills: ['JavaScript', 'Python', 'React', 'Node.js', 'SQL']
    },
    language: {
      english: {
        level: 'Advanced',
        testType: 'IELTS',
        scores: {
          overall: '7.5',
          listening: '7.0',
          reading: '8.0',
          writing: '7.0',
          speaking: '7.5'
        }
      },
      other: []
    },
    finances: {
      savings: '50000',
      income: '75000',
      currency: 'USD'
    },
    preferences: {
      targetCountries: ['Canada', 'Australia'],
      visaTypes: [],
      timeline: '1-2 years'
    }
  });

  const steps = [
    'Personal Information',
    'Education',
    'Work Experience',
    'Language Skills',
    'Financial Information',
    'Immigration Preferences',
    'Review & Submit'
  ];

  const countries = [
    'Canada', 'Australia', 'USA', 'Germany', 'New Zealand', 'UK', 
    'Netherlands', 'Sweden', 'Norway', 'Denmark', 'Switzerland'
  ];

  const educationLevels = [
    'High School',
    'Diploma/Certificate',
    "Bachelor's Degree",
    "Master's Degree",
    'PhD/Doctorate'
  ];

  const englishLevels = [
    'Basic',
    'Intermediate', 
    'Advanced',
    'Native'
  ];

  const testTypes = [
    'IELTS',
    'TOEFL',
    'PTE',
    'CELPIP',
    'Not taken yet'
  ];

  const industries = [
    'Technology/IT',
    'Healthcare',
    'Finance',
    'Engineering',
    'Education',
    'Marketing',
    'Sales',
    'Construction',
    'Manufacturing',
    'Other'
  ];

  const timelines = [
    'Within 6 months',
    '6-12 months',
    '1-2 years',
    '2+ years',
    'Just exploring options'
  ];

  const handleInputChange = (section, field, value) => {
    setProfileData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleNestedInputChange = (section, subsection, field, value) => {
    setProfileData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: {
          ...prev[section][subsection],
          [field]: value
        }
      }
    }));
  };

  const handleArrayInputChange = (section, field, value) => {
    setProfileData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleNext = () => {
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      await profileService.updateProfile(profileData);
      showSuccess('Profile completed successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to save profile:', error);
      showError('Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderPersonalInfo = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Personal Information
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Age"
            type="number"
            value={profileData.personal.age}
            onChange={(e) => handleInputChange('personal', 'age', e.target.value)}
            InputProps={{ inputProps: { min: 16, max: 100 } }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Nationality"
            value={profileData.personal.nationality}
            onChange={(e) => handleInputChange('personal', 'nationality', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Marital Status</InputLabel>
            <Select
              value={profileData.personal.maritalStatus}
              label="Marital Status"
              onChange={(e) => handleInputChange('personal', 'maritalStatus', e.target.value)}
            >
              <MenuItem value="single">Single</MenuItem>
              <MenuItem value="married">Married</MenuItem>
              <MenuItem value="divorced">Divorced</MenuItem>
              <MenuItem value="widowed">Widowed</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Number of Dependents"
            type="number"
            value={profileData.personal.dependents}
            onChange={(e) => handleInputChange('personal', 'dependents', parseInt(e.target.value) || 0)}
            InputProps={{ inputProps: { min: 0, max: 10 } }}
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderEducation = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Education Background
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Highest Education Level</InputLabel>
            <Select
              value={profileData.education.level}
              label="Highest Education Level"
              onChange={(e) => handleInputChange('education', 'level', e.target.value)}
            >
              {educationLevels.map(level => (
                <MenuItem key={level} value={level}>{level}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Field of Study"
            value={profileData.education.field}
            onChange={(e) => handleInputChange('education', 'field', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Institution Name"
            value={profileData.education.institution}
            onChange={(e) => handleInputChange('education', 'institution', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Country of Education"
            value={profileData.education.country}
            onChange={(e) => handleInputChange('education', 'country', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Year of Graduation"
            type="number"
            value={profileData.education.year}
            onChange={(e) => handleInputChange('education', 'year', e.target.value)}
            InputProps={{ inputProps: { min: 1950, max: new Date().getFullYear() } }}
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderWorkExperience = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Work Experience
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Total Years of Experience"
            type="number"
            value={profileData.workExperience.totalYears}
            onChange={(e) => handleInputChange('workExperience', 'totalYears', e.target.value)}
            InputProps={{ inputProps: { min: 0, max: 50 } }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Current Job Title"
            value={profileData.workExperience.currentRole}
            onChange={(e) => handleInputChange('workExperience', 'currentRole', e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Industry</InputLabel>
            <Select
              value={profileData.workExperience.industry}
              label="Industry"
              onChange={(e) => handleInputChange('workExperience', 'industry', e.target.value)}
            >
              {industries.map(industry => (
                <MenuItem key={industry} value={industry}>{industry}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Key Skills (comma-separated)"
            multiline
            rows={3}
            value={profileData.workExperience.skills.join(', ')}
            onChange={(e) => handleInputChange('workExperience', 'skills', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
            helperText="Enter your main professional skills separated by commas"
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderLanguageSkills = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Language Skills
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>English Proficiency Level</InputLabel>
            <Select
              value={profileData.language.english.level}
              label="English Proficiency Level"
              onChange={(e) => handleNestedInputChange('language', 'english', 'level', e.target.value)}
            >
              {englishLevels.map(level => (
                <MenuItem key={level} value={level}>{level}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>English Test Taken</InputLabel>
            <Select
              value={profileData.language.english.testType}
              label="English Test Taken"
              onChange={(e) => handleNestedInputChange('language', 'english', 'testType', e.target.value)}
            >
              {testTypes.map(test => (
                <MenuItem key={test} value={test}>{test}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {profileData.language.english.testType && profileData.language.english.testType !== 'Not taken yet' && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Overall Score"
                type="number"
                value={profileData.language.english.scores.overall}
                onChange={(e) => handleNestedInputChange('language', 'english', 'scores', {...profileData.language.english.scores, overall: e.target.value})}
                InputProps={{ inputProps: { min: 0, max: 9, step: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Listening Score"
                type="number"
                value={profileData.language.english.scores.listening}
                onChange={(e) => handleNestedInputChange('language', 'english', 'scores', {...profileData.language.english.scores, listening: e.target.value})}
                InputProps={{ inputProps: { min: 0, max: 9, step: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Reading Score"
                type="number"
                value={profileData.language.english.scores.reading}
                onChange={(e) => handleNestedInputChange('language', 'english', 'scores', {...profileData.language.english.scores, reading: e.target.value})}
                InputProps={{ inputProps: { min: 0, max: 9, step: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Writing Score"
                type="number"
                value={profileData.language.english.scores.writing}
                onChange={(e) => handleNestedInputChange('language', 'english', 'scores', {...profileData.language.english.scores, writing: e.target.value})}
                InputProps={{ inputProps: { min: 0, max: 9, step: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Speaking Score"
                type="number"
                value={profileData.language.english.scores.speaking}
                onChange={(e) => handleNestedInputChange('language', 'english', 'scores', {...profileData.language.english.scores, speaking: e.target.value})}
                InputProps={{ inputProps: { min: 0, max: 9, step: 0.5 } }}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );

  const renderFinances = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Financial Information
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Currency</InputLabel>
            <Select
              value={profileData.finances.currency}
              label="Currency"
              onChange={(e) => handleInputChange('finances', 'currency', e.target.value)}
            >
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="EUR">EUR</MenuItem>
              <MenuItem value="GBP">GBP</MenuItem>
              <MenuItem value="CAD">CAD</MenuItem>
              <MenuItem value="AUD">AUD</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Annual Income"
            type="number"
            value={profileData.finances.income}
            onChange={(e) => handleInputChange('finances', 'income', e.target.value)}
            InputProps={{ inputProps: { min: 0 } }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Available Savings"
            type="number"
            value={profileData.finances.savings}
            onChange={(e) => handleInputChange('finances', 'savings', e.target.value)}
            InputProps={{ inputProps: { min: 0 } }}
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderPreferences = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Immigration Preferences
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="body1" gutterBottom>
            Select your target countries:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {countries.map(country => (
              <Chip
                key={country}
                label={country}
                clickable
                color={profileData.preferences.targetCountries.includes(country) ? 'primary' : 'default'}
                onClick={() => {
                  const currentCountries = profileData.preferences.targetCountries;
                  const newCountries = currentCountries.includes(country)
                    ? currentCountries.filter(c => c !== country)
                    : [...currentCountries, country];
                  handleArrayInputChange('preferences', 'targetCountries', newCountries);
                }}
              />
            ))}
          </Box>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Immigration Timeline</InputLabel>
            <Select
              value={profileData.preferences.timeline}
              label="Immigration Timeline"
              onChange={(e) => handleInputChange('preferences', 'timeline', e.target.value)}
            >
              {timelines.map(timeline => (
                <MenuItem key={timeline} value={timeline}>{timeline}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );

  const renderReview = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review Your Information
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              Personal Information
            </Typography>
            <Typography variant="body2">Age: {profileData.personal.age}</Typography>
            <Typography variant="body2">Nationality: {profileData.personal.nationality}</Typography>
            <Typography variant="body2">Marital Status: {profileData.personal.maritalStatus}</Typography>
            <Typography variant="body2">Dependents: {profileData.personal.dependents}</Typography>
          </Paper>
          
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              Education
            </Typography>
            <Typography variant="body2">Level: {profileData.education.level}</Typography>
            <Typography variant="body2">Field: {profileData.education.field}</Typography>
            <Typography variant="body2">Institution: {profileData.education.institution}</Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              Work Experience
            </Typography>
            <Typography variant="body2">Experience: {profileData.workExperience.totalYears} years</Typography>
            <Typography variant="body2">Current Role: {profileData.workExperience.currentRole}</Typography>
            <Typography variant="body2">Industry: {profileData.workExperience.industry}</Typography>
          </Paper>
          
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              Preferences
            </Typography>
            <Typography variant="body2">Target Countries: {profileData.preferences.targetCountries.join(', ')}</Typography>
            <Typography variant="body2">Timeline: {profileData.preferences.timeline}</Typography>
          </Paper>
        </Grid>
      </Grid>
      
      <Alert severity="info" sx={{ mt: 3 }}>
        Please review all information carefully. You can always update your profile later from the dashboard.
      </Alert>
    </Box>
  );

  const getStepContent = (step) => {
    switch (step) {
      case 0: return renderPersonalInfo();
      case 1: return renderEducation();
      case 2: return renderWorkExperience();
      case 3: return renderLanguageSkills();
      case 4: return renderFinances();
      case 5: return renderPreferences();
      case 6: return renderReview();
      default: return 'Unknown step';
    }
  };

  const isStepValid = (step) => {
    switch (step) {
      case 0:
        return profileData.personal.age && profileData.personal.nationality;
      case 1:
        return profileData.education.level && profileData.education.field;
      case 2:
        return profileData.workExperience.totalYears && profileData.workExperience.currentRole;
      case 3:
        return profileData.language.english.level;
      case 4:
        return true; // Optional step
      case 5:
        return profileData.preferences.targetCountries.length > 0;
      case 6:
        return true;
      default:
        return false;
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Typography
          variant="h3"
          component="h1"
          sx={{ textAlign: 'center', mb: 2, fontWeight: 600 }}
        >
          Immigration Profile Setup
        </Typography>
        
        <Typography
          variant="h6"
          sx={{ textAlign: 'center', mb: 4, color: 'text.secondary' }}
        >
          Complete your profile step by step to get personalized immigration recommendations
        </Typography>

        <Box sx={{ mb: 4 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Paper sx={{ p: 4, mb: 4 }}>
          {getStepContent(activeStep)}
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            startIcon={<NavigateBefore />}
          >
            Back
          </Button>

          <Box sx={{ flex: '1 1 auto' }} />

          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
            >
              {loading ? 'Saving...' : 'Complete Profile'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!isStepValid(activeStep)}
              endIcon={<NavigateNext />}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default Onboarding;